import React, {useEffect, useRef, useState} from 'react';
import './App.scss';
import {Header} from "./Header";
import {GameArea} from "./GameArea";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Login from "./components/Login/Login";
import Register from "./components/Register/Register";
import Spinner from "./components/Spinner";
import {useAppDispatch, useAppSelector} from "./hooks";
import {useGuestMutation, useSendLogoutMutation, useWhoAmIQuery} from './features/api/authentication-api';
import {describeApiError} from "./features/api/errors";
import {login, logout} from "./features/authentication/authentication-slice";
import {SocketState} from "./features/socketio/socket-state";
import {GameStore, useGameStore} from "./features/game/game-store";
import Match from "./Match";
import SettingsPanel from "./components/Settings/SettingsPanel";
import {BACKEND_ENABLED} from "./config";
import Events from "./Events";
import RulesetPicker, {useChosenRuleset} from "./components/RulesetPicker/RulesetPicker";
import {PlayerSettings} from "./settings";
import {RULESET_NAMES, Ruleset} from "./shared/rules";

function App() {
    const authentication = useAppSelector(state => state.authentication);
    const dispatch = useAppDispatch();
    const loggedIn = !!authentication.accessToken && !!authentication.username;
    const [sendLogout] = useSendLogoutMutation();
    const [guestLogin, guestResult] = useGuestMutation();
    const whoAmI = useWhoAmIQuery(authentication.username, {skip: !BACKEND_ENABLED || !loggedIn});

    const mode = useGameStore(s => s.mode);
    const status = useGameStore(s => s.status);
    const searching = useGameStore(s => s.searching);
    const connection = useGameStore(s => s.connection);
    const ruleset = useChosenRuleset();

    const [showSettings, setShowSettings] = useState(false);
    const [modal, setModal] = useState<{show: "none" | "login" | "register", notice?: string}>({show: "none"});
    const [guestError, setGuestError] = useState<string | null>(null);
    const [findError, setFindError] = useState<string | null>(null);
    const connectedOnce = useRef(false);

    const openSettings = () => {
        try { document.exitPointerLock && document.exitPointerLock(); } catch {}
        setShowSettings(true);
    };

    // Keyboard shortcut for Settings: O (the game itself only uses Space and the arrow keys). Ignored while
    // typing in a form, with any modifier key, while another dialog is open, and mid-stroke (Space held) so a shot is never interrupted.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.code !== "KeyO" || e.repeat) return;
            // plain O only: any modifier (Shift, Ctrl, Alt, AltGr, Meta/Cmd) leaves the key to the browser / OS
            if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey || e.getModifierState?.("AltGraph")) return;
            const el = e.target as HTMLElement | null;
            if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
            if (document.querySelector(".modal.show") || (Events as any).keysPressed?.space) return;
            e.preventDefault();
            openSettings();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    // Restore a saved session: connect the socket (rejoins a running game) and validate the token.
    useEffect(() => {
        if (BACKEND_ENABLED && loggedIn && !connectedOnce.current) {
            connectedOnce.current = true;
            SocketState.connect();
        }
    }, [loggedIn]);

    useEffect(() => {
        const err: any = whoAmI.error;
        if (err && err.status === 401) {
            SocketState.disconnect();
            dispatch(logout());
            connectedOnce.current = false;
            GameStore.toast("Your session expired. Please sign in again.", "warn", 6000);
        }
    }, [whoAmI.error, dispatch]);

    // Refresh the win/loss record when a match finishes.
    const refetch = whoAmI.refetch;
    useEffect(() => {
        if (mode === "online" && status === "over" && loggedIn && !authentication.guest) {
            const t = setTimeout(() => { try { refetch(); } catch {} }, 600);
            return () => clearTimeout(t);
        }
    }, [mode, status, loggedIn, authentication.guest, refetch]);

    const signedIn = () => {
        setModal({show: "none"});
        connectedOnce.current = true;
        SocketState.connect();
        GameStore.toast("Signed in. Press Find match to play someone online.", "good");
    };

    const playAsGuest = () => {
        setGuestError(null);
        guestLogin().unwrap().then(result => {
            dispatch(login({newState: {...result, id: null}}));
            signedIn();
        }).catch(error => setGuestError(describeApiError(error, "guest")));
    };

    const logoutClicked = () => {
        Match.backToMenu();
        SocketState.disconnect();
        connectedOnce.current = false;
        sendLogout(authentication.refreshToken || "");
        dispatch(logout());
    };

    const findMatchClicked = () => {
        setFindError(null);
        SocketState.findMatch(PlayerSettings.get().ruleset).then((r: any) => {
            if (!r || r.status === "offline") setFindError("Not connected to the game server yet. Retrying…");
            else if (r.status === "error") setFindError(r.error || "Could not join the queue.");
        });
    };

    const inOnlineGame = mode === "online" && status === "playing";
    const stats = whoAmI.data?.stats;
    const rulesetLocked = inOnlineGame || searching;

    /** Snooker | Any ball: remembered for practice and Find match; a practice frame on the table restarts with it. */
    const chooseRuleset = (next: Ruleset) => {
        if (next === PlayerSettings.get().ruleset || rulesetLocked) return;
        if (mode === "practice" && Match.frame.shots > 0 && status === "playing"
            && !window.confirm(`Switch to ${RULESET_NAMES[next]}? Your current practice frame will be reset.`)) return;
        PlayerSettings.set({ruleset: next});
        if (mode === "practice") Match.startPractice(next);
    };
    const rulesetPicker = <>
        <div className="sidebar-label">Rules</div>
        <RulesetPicker id="ruleset-picker" value={ruleset} disabled={rulesetLocked} onChange={chooseRuleset}/>
    </>;

    return <>
        <div id="audios" style={{display: "none"}}>
            <audio id="ball-hit">
                <source src='../Models/Sounds/shot2.ogg'/>
                <source src='../Models/Sounds/shot2.mp3'/>
            </audio>
        </div>
        <Header singleDisabled={inOnlineGame}
                ruleset={ruleset}
                rulesetDisabled={rulesetLocked}
                onRuleset={() => chooseRuleset(ruleset === "anyball" ? "snooker" : "anyball")}
                onSettings={openSettings}
                onSingle={() => {
                    if (inOnlineGame) return;
                    if (mode === "practice" && Match.frame.shots > 0 && status === "playing"
                        && !window.confirm("Start a new practice frame? Your current practice frame will be reset.")) return;
                    Match.startPractice();
                }}
                onMultiplayer={() => {
                    if (!BACKEND_ENABLED || inOnlineGame) return;
                    if (!loggedIn) setModal({show: "login"});
                    else if (!searching) findMatchClicked();
                }}/>
        <div id="main-area">
            <GameArea></GameArea>
            {BACKEND_ENABLED && <div id="sidebar">
                { !loggedIn ?
                    <>
                        <div className="sidebar-title">Play online</div>
                        <Button className="w-100" id="btn-login" onClick={() => setModal({show: "login"})}>Sign in</Button>
                        <Button className="w-100 mt-2" id="btn-open-register" variant="light" onClick={() => setModal({show: "register"})}>Register</Button>
                        <Button className="w-100 mt-2" id="btn-guest" variant="outline-light" disabled={guestResult.isLoading} onClick={playAsGuest}>
                            {guestResult.isLoading ? <><Spinner/>Joining…</> : "Play as guest"}
                        </Button>
                        {guestError ? <div className="sidebar-error" role="alert">{guestError}</div> : null}
                        <div className="sidebar-note">Or just practise on this table: no account needed.</div>
                        {rulesetPicker}
                    </> :
                    <>
                        <div id="sidebar-grow">
                            <div className="welcome">Welcome, <b id="player-name">{authentication.username}</b>
                                {authentication.guest ? <span className="guest-tag">guest</span> : null}</div>
                            {authentication.guest
                                ? <div className="record">Guest games aren't recorded. Register to keep a win/loss record.</div>
                                : <div className="record" id="record">
                                    {stats ? <>Record: <b>{stats.wins}</b> W / <b>{stats.losses}</b> L</> : whoAmI.isFetching ? "Loading record…" : null}
                                </div>}
                            {!authentication.guest && stats?.elo ? <div className="record" id="elo-ratings">
                                Elo: {(Object.keys(RULESET_NAMES) as Ruleset[]).filter(r => stats.elo![r]).map((r, i) =>
                                    <span key={r} className={"elo-rating" + (r === ruleset ? " current" : "")} data-ruleset={r}>
                                        {i ? " · " : ""}{RULESET_NAMES[r]} <b>{stats.elo![r].rating}</b>
                                    </span>)}
                            </div> : null}
                            <div className={"conn conn-" + connection}>
                                {connection === "online" ? "Connected" : connection === "connecting" ? "Connecting…" : "Offline"}
                            </div>
                            {rulesetPicker}
                            {inOnlineGame ? <>
                                <Button className="w-100 mt-2" id="btn-concede" variant="warning" onClick={() => {
                                    if (window.confirm("Concede this frame? Your opponent will win.")) Match.concede();
                                }}>Concede frame</Button>
                            </> : searching ? <>
                                <div className="searching"><Spinner/>Looking for {authentication.guest ? "a casual" : "a ranked"} {ruleset === "anyball" ? "Any ball" : "snooker"} opponent…</div>
                                <Button className="w-100 mt-2" id="btn-cancel-match" variant="outline-light" onClick={() => SocketState.cancelMatch()}>Cancel</Button>
                            </> : <>
                                <Button className="w-100 mt-2" id="btn-find-match" variant="success" disabled={connection !== "online"} onClick={findMatchClicked}>Find match</Button>
                                <div className="sidebar-note" id="match-kind-note">{authentication.guest
                                    ? "Guests play casual (unranked) frames against other guests. Sign in or register to play ranked and get an Elo rating."
                                    : "Ranked: you play other signed-in players, 60 s per shot, and the result changes your Elo."}</div>
                                {mode === "online" ? <Button className="w-100 mt-2" variant="outline-light" onClick={() => Match.backToMenu()}>Back to practice</Button> : null}
                            </>}
                            {findError ? <div className="sidebar-error" role="alert">{findError}</div> : null}
                            {stats && stats.recent.length ? <div className="recent">
                                <div className="recent-title">Recent</div>
                                {stats.recent.map((r, i) => <div key={i} className={"recent-row " + (r.won ? "w" : "l")}>
                                    <span>{r.won ? "W" : "L"}</span><span className="opp">{r.opponent}{r.ruleset === "anyball" ? <span className="ruleset-tag" title="Any ball">AB</span> : null}</span><span>{r.score}-{r.opponentScore}</span>
                                </div>)}
                            </div> : null}
                        </div>
                        <Button id="btn-logout" variant="secondary" className="w-100 mt-2" onClick={logoutClicked}>
                            {authentication.guest ? "Leave" : "Logout"}
                        </Button>
                    </>
                }
            </div>}
        </div>
        {BACKEND_ENABLED && <>
        <Modal id="login-modal" centered show={modal.show === "login"} onHide={() => setModal({show: "none"})}>
            <Modal.Body>
                <Login notice={modal.notice} loginSuccess={signedIn} registerClicked={() => setModal({show: "register"})}/>
            </Modal.Body>
        </Modal>
        <Modal id="register-modal" centered show={modal.show === "register"} onHide={() => setModal({show: "none"})}>
            <Modal.Body>
                <Register registerSuccess={signedIn} loginClicked={(notice?: string) => setModal({show: "login", notice})}/>
            </Modal.Body>
        </Modal>
        </>}
        <SettingsPanel show={showSettings} onHide={() => setShowSettings(false)}/>
    </>;
}

export default App;
